import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAxios } from "../useAxios";
import { USER_API } from "config";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { useProjectAuth } from "hooks/project/useProjectAuth";
import {
  CharacterListState,
  CharacterState,
} from "components/dashboard/body/projects/panels/character/types";
import { characterKeys } from "queries/character.keys";

/** The API body: `status_code` 200 carries the data; any other code is a failed request. */
interface CharactersResponse<T> {
  status_code?: number;
  message?: string;
  data?: T;
}

const NO_CHARACTERS: CharacterListState[] = [];

/** The body's data, or a thrown error so the query reports the failure instead of no rows. */
function successfulData<T>(
  response: CharactersResponse<T> | undefined,
  what: string
): T | undefined {
  if (response?.status_code !== 200) {
    throw new Error(response?.message || `Couldn't load ${what}`);
  }
  return response.data;
}

/**
 * Hook to fetch the list of characters. `characters` stays empty until the list loads; read
 * `loadFailed` to tell a failed first load from a script with no characters.
 */
export const useCharacterList = () => {
  const axios = useAxios();
  const { projectId, documentId, hasValidIds } = useProjectIdentifiers();
  const { isLoading: isRoleLoading } = useProjectAuth(projectId);

  // Enable character loading when:
  // 1. We have valid project/document IDs
  // 2. Role loading is complete (not loading)
  // Note: Viewers can see characters but cannot edit them
  const enabled = hasValidIds && !isRoleLoading;

  const charactersQuery = useQuery({
    queryKey: characterKeys.list(projectId || 0, documentId || ""),
    queryFn: async (): Promise<CharacterListState[]> => {
      if (!hasValidIds) return [];

      const response: CharactersResponse<{ characters?: CharacterListState[] }> = await axios.get(
        USER_API.getCharacters(projectId + "", documentId + "")
      );
      return successfulData(response, "characters")?.characters ?? [];
    },
    enabled,
  });
  const { data, isError, isFetching, refetch } = charactersQuery;

  // Memoize the characters array to prevent unnecessary re-renders
  const characters = useMemo(() => data ?? NO_CHARACTERS, [data]);

  return {
    characters,
    isLoading: enabled ? charactersQuery.isLoading : false,
    /**
     * The last request failed and left no rows to show. Loaded rows stay after a failed refresh;
     * a cached empty list does not, since it cannot tell "no characters" from "couldn't load".
     */
    loadFailed: enabled && isError && characters.length === 0,
    isFetching,
    refetch,
  };
};

/**
 * Hook to fetch all characters with detailed information. `allCharactersDetailed` is undefined
 * until it loads; a failed request leaves it undefined instead of an empty list. Read
 * `allCharactersLoadFailed` before treating no characters as none.
 */
export const useAllCharactersDetailed = () => {
  const axios = useAxios();
  const { projectId, documentId, hasValidIds } = useProjectIdentifiers();
  const { isLoading: isRoleLoading } = useProjectAuth(projectId);

  // Enable character loading when:
  // 1. We have valid project/document IDs
  // 2. Role loading is complete (not loading)
  // Note: Viewers can see characters but cannot edit them
  const enabled = hasValidIds && !isRoleLoading;

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: characterKeys.allDetailed(projectId || 0, documentId || ""),
    queryFn: async (): Promise<CharacterState[]> => {
      if (!hasValidIds) return [];

      const response: CharactersResponse<CharacterState[]> = await axios.get(
        USER_API.getAllCharactersDetailed(projectId + "", documentId + "")
      );
      return successfulData(response, "character details") ?? [];
    },
    enabled,
  });

  return {
    allCharactersDetailed: data,
    isLoadingAllCharacters: enabled ? isLoading : false,
    /** The last request failed and left no characters to show, the same rule as `loadFailed`. */
    allCharactersLoadFailed: enabled && isError && !data?.length,
    isFetchingAllCharacters: isFetching,
    refetchAllCharacters: refetch,
  };
};

// getUseCharacter function removed - use useCharacterDetails hook directly in components
